import React, { useState, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useSystemDate } from '../hooks/useSystemDate';

export const LoanManagementView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();
  
  const employees = useLiveQuery(() => 
    activeCompany ? db.employees.where('companyId').equals(activeCompany.id!).and(e => e.status === 'Active').toArray() : []
  , [activeCompany]);

  const loans = useLiveQuery(() => 
    activeCompany ? db.employeeLoans.where('companyId').equals(activeCompany.id!).toArray() : []
  , [activeCompany]);

  const [activeTab, setActiveTab] = useState<'issue' | 'list'>('list');
  
  // Issue Loan State
  const [selectedEmpId, setSelectedEmpId] = useState<number | ''>('');
  const [principalAmount, setPrincipalAmount] = useState<string | number>('');
  const [interestRate, setInterestRate] = useState<string | number>('0');
  const [termMonths, setTermMonths] = useState<string | number>('12');
  const [issueDate, setIssueDate] = useState(systemDate);
  const [deductionStarts, setDeductionStarts] = useState(systemDate);
  
  // List State
  const scrollRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const flatItems = (loans || []).reverse();
  
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => {});

  const handleIssueLoan = async () => {
    if (!selectedEmpId || !principalAmount || !termMonths || !activeCompany) {
      alert('Please fill all required fields');
      return;
    }

    try {
      // Calculate EMI (Simple interest for now)
      const p = Number(principalAmount);
      const r = Number(interestRate);
      const t = Number(termMonths);
      
      const totalInterest = (p * r * (t/12)) / 100;
      const totalAmount = p + totalInterest;
      const emi = totalAmount / t;

      await db.employeeLoans.add({
        companyId: activeCompany.id!,
        employeeId: Number(selectedEmpId),
        amount: p,
        emiAmount: emi,
        totalMonths: t,
        monthsPaid: 0,
        startDate: issueDate,
        status: 'Active'
      });
      
      alert('Loan issued successfully.');
      setSelectedEmpId('');
      setPrincipalAmount('');
      setInterestRate('0');
      setActiveTab('list');
    } catch (e: any) {
      alert(e?.message || 'Failed to issue loan.');
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-20 absolute inset-0 font-sans text-[13px] text-gray-800">
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px]">
        <span className="w-1/3 text-left">Loan / Advance Management</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex bg-[#d9e4d5] border-b border-[#1e8585] font-bold">
         <div 
           className={`px-6 py-2 cursor-pointer ${activeTab === 'list' ? 'bg-white text-[#1e8585] border-t-2 border-[#1e8585]' : 'text-gray-700 hover:bg-white hover:text-[#1e8585]'}`}
           onClick={() => setActiveTab('list')}
         >
           Active & Closed Loans
         </div>
         <div 
           className={`px-6 py-2 cursor-pointer ${activeTab === 'issue' ? 'bg-white text-[#1e8585] border-t-2 border-[#1e8585]' : 'text-gray-700 hover:bg-white hover:text-[#1e8585]'}`}
           onClick={() => setActiveTab('issue')}
         >
           Issue New Loan
         </div>
      </div>

      <div className="flex-1 p-4 overflow-hidden">
         {activeTab === 'issue' && (
           <div className="bg-white border border-[#1e8585] p-6 w-[600px] shadow-md mx-auto">
              <h2 className="text-[15px] font-bold text-[#1e8585] border-b border-gray-300 pb-2 mb-4">Issue Loan or Advance Salary</h2>
              
              <div className="flex flex-col gap-4">
                 <div className="flex flex-col">
                   <label className="font-bold text-gray-700 mb-1">Select Employee</label>
                   <select 
                     value={selectedEmpId} 
                     onChange={e => setSelectedEmpId(e.target.value === '' ? '' : Number(e.target.value))}
                     className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                   >
                     <option value="">-- Select Employee --</option>
                     {employees?.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employeeId})</option>)}
                   </select>
                 </div>

                 <div className="flex gap-4">
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">Principal Amount (₹)</label>
                     <input 
                       type="number" 
                       value={principalAmount} 
                       onChange={e => setPrincipalAmount(e.target.value)}
                       className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                       placeholder="e.g. 50000"
                     />
                   </div>
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">Interest Rate (% p.a.)</label>
                     <input 
                       type="number" 
                       value={interestRate} 
                       onChange={e => setInterestRate(e.target.value)}
                       className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                       placeholder="e.g. 0 for No Interest"
                     />
                   </div>
                 </div>

                 <div className="flex gap-4">
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">Repayment Term (Months)</label>
                     <input 
                       type="number" 
                       value={termMonths} 
                       onChange={e => setTermMonths(e.target.value)}
                       className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                     />
                   </div>
                   <div className="flex flex-col flex-1">
                     <label className="font-bold text-gray-700 mb-1">Issue Date</label>
                     <input 
                       type="date" 
                       value={issueDate} 
                       onChange={e => setIssueDate(e.target.value)}
                       className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                     />
                   </div>
                 </div>

                 <div className="flex flex-col">
                   <label className="font-bold text-gray-700 mb-1">Deduction Starts From (Month)</label>
                   <input 
                     type="month" 
                     value={deductionStarts.substring(0, 7)} 
                     onChange={e => setDeductionStarts(e.target.value + '-01')}
                     className="border border-gray-300 px-2 py-1.5 outline-none focus:border-[#1e8585]"
                   />
                   <span className="text-[11px] text-gray-500 mt-1">EMI will be automatically deducted during payroll processing from this month.</span>
                 </div>

                 {principalAmount && termMonths && (
                   <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mt-2 text-[#1e8585] font-bold flex justify-between">
                     <span>Estimated EMI:</span>
                     <span>₹ {(((Number(principalAmount) + (Number(principalAmount) * Number(interestRate) * (Number(termMonths)/12)) / 100)) / Number(termMonths)).toFixed(2)} / month</span>
                   </div>
                 )}

                 <button 
                   onClick={handleIssueLoan}
                   className="mt-4 bg-[#1e8585] text-white font-bold py-2.5 hover:bg-[#156666] shadow"
                 >
                   Issue Loan to Employee
                 </button>
              </div>
           </div>
         )}

         {activeTab === 'list' && (
           <div className="bg-white border border-[#1e8585] h-full flex flex-col shadow-sm">
             <div className="flex bg-[#d9e4d5] px-2 py-2 font-bold border-b border-gray-400 text-black">
                <div className="w-[180px]">Employee</div>
                <div className="w-[120px] text-right">Principal</div>
                <div className="w-[100px] text-center">Term & Rate</div>
                <div className="w-[120px] text-right text-red-700">EMI</div>
                <div className="w-[120px] text-right">Remaining</div>
                <div className="w-[120px] text-center">Status</div>
             </div>
             
             <div ref={scrollRef} className="flex-1 overflow-y-auto">
               {flatItems.length === 0 ? (
                 <div className="p-4 text-center text-gray-500 italic">No loans found.</div>
               ) : (
                 flatItems.map((loan, idx) => {
                   const emp = employees?.find(e => e.id === loan.employeeId);
                   const isSelected = selectedIndex === idx;
                   return (
                     <div 
                       key={loan.id} 
                       className={`flex px-2 py-2.5 border-b border-gray-200 items-center ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                       onClick={() => setSelectedIndex(idx)}
                     >
                        <div className="w-[180px] font-bold text-[#1e8585]">{emp?.name || 'Unknown'}</div>
                        <div className="w-[120px] text-right">₹ {loan.amount.toFixed(2)}</div>
                        <div className="w-[100px] text-center text-[12px]">{loan.totalMonths}M</div>
                        <div className="w-[120px] text-right font-bold text-red-600">₹ {loan.emiAmount.toFixed(2)}</div>
                        <div className="w-[120px] text-right font-bold">₹ {(loan.amount - (loan.emiAmount * loan.monthsPaid)).toFixed(2)}</div>
                        <div className="w-[120px] text-center">
                          <span className={`px-2 py-[2px] rounded text-[11px] font-bold text-white ${loan.status === 'Active' ? 'bg-green-600' : 'bg-gray-500'}`}>
                            {loan.status}
                          </span>
                        </div>
                     </div>
                   );
                 })
               )}
             </div>
           </div>
         )}
      </div>
    </div>
  );
};
